/* eslint eslint-comments/no-use: off */
/* eslint-disable @github-ui/github-components/require-component-stories */
import type {RepoOverviewBlobPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {sendEvent} from '@github-ui/hydro-analytics'
import {
  codeOfConductPath,
  editBlobPath,
  licenseSelectorPath,
  repositoryTreePath,
  securityPolicyPath,
} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {BookIcon, CodeOfConductIcon, LawIcon, ListUnorderedIcon} from '@primer/octicons-react'
import {ActionMenu, Box, Button, Heading, Octicon, Text} from '@primer/react'
import {UnderlineNav} from '@primer/react/experimental'
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'

import {useFilesPageInfo} from '../../hooks/FilesPageInfo'
import TableOfContentsPanel from '../blob/BlobContent/Renderable/TableOfContentsPanel'
import {EditButton} from '../blob/BlobHeader'
import {DirectoryRichtextContent} from '../directory/DirectoryContent/DirectoryReadmePreview'

export function OverviewFiles({
  readme,
  coc,
  licenses,
  securityPolicy,
  shouldRecommendReadme,
  isPersonalRepo,
  processingTime,
}: {
  readme?: RepoOverviewBlobPayload
  coc?: RepoOverviewBlobPayload
  licenses: RepoOverviewBlobPayload[]
  securityPolicy?: RepoOverviewBlobPayload
  shouldRecommendReadme: boolean
  isPersonalRepo: boolean
  processingTime: number
}) {
  let initialSelectedTab = 'readme-ov-file'
  if (!readme && !shouldRecommendReadme) {
    if (coc) {
      initialSelectedTab = 'coc-ov-file'
    } else if (licenses[0]) {
      initialSelectedTab = `${licenses[0].tabName}-1-ov-file`
    } else if (securityPolicy) {
      initialSelectedTab = 'security-ov-file'
    }
  }

  const [selectedTab, setSelectedTab] = useState(initialSelectedTab)
  const containerRef = useRef<HTMLDivElement>(null)
  const repo = useCurrentRepository()
  const {refInfo} = useFilesPageInfo()

  useEffect(() => {
    if (!readme && !coc && !licenses.length && !securityPolicy) return
    sendEvent('overview-repo-files', {
      'file-count': [readme, coc, ...licenses, securityPolicy].filter(file => file !== undefined).length,
      'timed-out':
        readme?.timedOut || coc?.timedOut || licenses.some(license => license.timedOut) || securityPolicy?.timedOut,
      'processing-time': processingTime,
    })
  }, [coc, licenses, processingTime, readme, securityPolicy])

  const getTabNames = useCallback(() => {
    const tabNames: string[] = []

    if (readme || shouldRecommendReadme) {
      tabNames.push('readme-ov-file')
    }

    if (coc || (shouldRecommendReadme && !isPersonalRepo)) {
      tabNames.push('coc-ov-file')
    }

    if (licenses.length) {
      for (const [index, license] of licenses.entries()) {
        tabNames.push(`${license.tabName}-${index + 1}-ov-file`)
      }
    } else if (shouldRecommendReadme && !isPersonalRepo) {
      tabNames.push('license-ov-file')
    }

    if (securityPolicy || (shouldRecommendReadme && !isPersonalRepo)) {
      tabNames.push('security-ov-file')
    }

    return tabNames
  }, [coc, isPersonalRepo, licenses, readme, securityPolicy, shouldRecommendReadme])

  useLayoutEffect(() => {
    if (!readme && !coc && !licenses.length && !securityPolicy) return

    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '')

      const tabNames = getTabNames()

      if (hash && tabNames.includes(hash)) {
        // Move the hash to the query param and set the hash to the readme section id
        const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?tab=${hash}#readme`
        history.replaceState(null, '', newUrl)
        setSelectedTab(hash)
      } else {
        const params = new URLSearchParams(window.location.search)
        const tabName = params.get('tab')

        if (tabName && tabNames.includes(tabName)) {
          setSelectedTab(tabName)
        }
      }

      window.requestAnimationFrame(() => {
        if (containerRef.current && window.location.hash === '#readme') {
          containerRef.current.scrollIntoView()
        }
      })
    }

    onHashChange()

    window.addEventListener('hashchange', onHashChange)

    return () => {
      window.removeEventListener('hashchange', onHashChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSelect = useCallback(
    (ev: React.MouseEvent | React.KeyboardEvent, tabId: string) => {
      ev.preventDefault()
      if (selectedTab === tabId) return

      setSelectedTab(tabId)
      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?tab=${tabId}`
      history.replaceState(null, '', newUrl)
    },
    [selectedTab],
  )

  if (!readme && !coc && !licenses.length && !securityPolicy && !shouldRecommendReadme) {
    return null
  }

  let content: JSX.Element | undefined = undefined

  let blob: RepoOverviewBlobPayload | undefined = undefined

  const selectedLicense = licenses.find((license, index) => `${license.tabName}-${index + 1}-ov-file` === selectedTab)

  if (selectedTab === 'readme-ov-file') {
    if (readme) {
      blob = readme
    } else if (shouldRecommendReadme) {
      content = (
        <MissingContent
          title="Add a README"
          description={
            isPersonalRepo
              ? 'Add a README with an overview of your project.'
              : 'Help people interested in this repository understand your project by adding a README.'
          }
          icon={BookIcon}
          buttonText="Add a README"
          href={`${repositoryTreePath({
            repo,
            path: undefined,
            commitish: refInfo.name,
            action: 'new',
          })}?filename=README.md`}
        />
      )
    }
  } else if (selectedTab === 'coc-ov-file') {
    if (coc) {
      blob = coc
    } else if (shouldRecommendReadme && !isPersonalRepo) {
      content = (
        <MissingContent
          title="Add a code of conduct"
          description="Define community standards, signal a welcoming and inclusive project, and outline procedures for handling abuse by adding a code of conduct."
          icon={CodeOfConductIcon}
          buttonText="Add a code of conduct"
          href={codeOfConductPath(repo.ownerLogin, repo.name)}
        />
      )
    }
  } else if (selectedLicense) {
    blob = selectedLicense
  } else if (selectedTab === 'license-ov-file' && shouldRecommendReadme && !isPersonalRepo) {
    content = (
      <MissingContent
        title="Add a license"
        description="Add a license to your repository to make it clear how others can use, change, and contribute to your project."
        icon={LawIcon}
        buttonText="Add a license"
        href={licenseSelectorPath(repo.ownerLogin, repo.name)}
      />
    )
  } else {
    if (securityPolicy) {
      blob = securityPolicy
    } else if (shouldRecommendReadme && !isPersonalRepo) {
      content = (
        <MissingContent
          title="Add a security policy"
          description="Help your community understand how to securely report security vulnerabilities for your project."
          icon={LawIcon}
          buttonText="Add a security policy"
          href={securityPolicyPath(repo.ownerLogin, repo.name)}
        />
      )
    }
  }

  if (blob && blob.path) {
    content = (
      <DirectoryRichtextContent
        errorMessage={blob.errorMessage}
        onAnchorClick={() => {
          const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?tab=${selectedTab}${window.location.hash}`
          history.replaceState(null, '', newUrl)
        }}
        path={blob.path}
        richText={blob.richText}
        stickyHeaderHeight={50}
        timedOut={blob.timedOut}
      />
    )
  }

  return (
    <Box sx={{display: 'flex', flexGrow: 1, gap: 3}}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'border.default',
          borderRadius: '6px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          '@media screen and (max-width: 543px)': {
            mx: '-16px',
            maxWidth: 'calc(100% + 32px)',
          },
          '@media screen and (min-width: 544px)': {
            maxWidth: '100%',
          },
        }}
        ref={containerRef}
      >
        <Box
          sx={{
            display: 'flex',
            borderBottom: '1px solid',
            borderBottomColor: 'border.default',
            alignItems: 'center',
            pr: 2,
            position: 'sticky',
            top: 0,
            backgroundColor: 'canvas.default',
            zIndex: 1,
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px',
          }}
        >
          <UnderlineNav aria-label="Repository files" sx={{flexGrow: 1, borderBottom: 'none', maxWidth: '100%', px: 2}}>
            {(readme || shouldRecommendReadme) && (
              <UnderlineNav.Item
                icon={BookIcon}
                aria-current={selectedTab === 'readme-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'readme-ov-file')}
              >
                {readme?.tabName || 'README'}
              </UnderlineNav.Item>
            )}
            {(coc || (shouldRecommendReadme && !isPersonalRepo)) && (
              <UnderlineNav.Item
                icon={CodeOfConductIcon}
                aria-current={selectedTab === 'coc-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'coc-ov-file')}
              >
                {coc?.tabName || 'Code of conduct'}
              </UnderlineNav.Item>
            )}
            {licenses.length ? (
              licenses.map((license, index) => {
                const tabId = `${license.tabName}-${index + 1}-ov-file`

                return (
                  <UnderlineNav.Item
                    key={license.path}
                    icon={LawIcon}
                    aria-current={selectedTab === tabId ? 'page' : undefined}
                    onSelect={ev => onSelect(ev, tabId)}
                  >
                    {license.tabName.toLowerCase() !== 'license' ? `${license.tabName} license` : 'License'}
                  </UnderlineNav.Item>
                )
              })
            ) : shouldRecommendReadme && !isPersonalRepo ? (
              <UnderlineNav.Item
                icon={LawIcon}
                aria-current={selectedTab === 'license-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'license-ov-file')}
              >
                License
              </UnderlineNav.Item>
            ) : null}

            {(securityPolicy || (shouldRecommendReadme && !isPersonalRepo)) && (
              <UnderlineNav.Item
                icon={LawIcon}
                aria-current={selectedTab === 'security-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'security-ov-file')}
              >
                {securityPolicy?.tabName || 'Security policy'}
              </UnderlineNav.Item>
            )}
          </UnderlineNav>
          {refInfo.canEdit && blob && (
            <EditButton
              disabled={false}
              editPath={editBlobPath({
                owner: repo.ownerLogin,
                repo: blob.repoName,
                commitish: blob.refName,
                filePath: blob.path,
              })}
              editTooltip="Edit README"
              loggedIn={true}
              siteNavLoginPath=""
              customSx={{mr: 2, color: 'var(--color-fg-muted)', height: '28px'}}
              buttonVariant="invisible"
            />
          )}
          {blob?.headerInfo?.toc && blob?.headerInfo?.toc?.length >= 2 && (
            <ActionMenu>
              <ActionMenu.Button
                icon={ListUnorderedIcon}
                variant="invisible"
                aria-label="Outline"
                sx={{color: 'fg.subtle', px: 2}}
              >
                Outline
              </ActionMenu.Button>

              <ActionMenu.Overlay align="end" sx={{minWidth: '256px'}}>
                <TableOfContentsPanel toc={blob.headerInfo.toc} />
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
        </Box>
        {content}
      </Box>
    </Box>
  )
}

function MissingContent({
  title,
  description,
  icon,
  buttonText,
  href,
}: {
  title: string
  description: string
  icon: React.ElementType
  buttonText: string
  href: string
}) {
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, px: 3}}>
      <Octicon icon={icon} size={32} sx={{color: 'fg.subtle', mb: 3}} />
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 5}}>
        <Heading as="h2" sx={{fontSize: 4}}>
          {title}
        </Heading>
        <Text sx={{fontSize: 0, color: 'fg.subtle', textAlign: 'center'}}>{description}</Text>
      </Box>
      <Button as={Link} to={href} variant="primary" reloadDocument>
        {buttonText}
      </Button>
    </Box>
  )
}

try{ OverviewFiles.displayName ||= 'OverviewFiles' } catch {}
try{ MissingContent.displayName ||= 'MissingContent' } catch {}